import type { Application, TemplateCategory, TemplateListItem } from '@/api/types'
import { byOrder } from '@/shared/lib/order'

/** `all`, the company's own templates, its archived ones, or one category id. */
export type TemplateFilter = 'all' | 'company' | 'archived' | (string & {})

const OWN_FILTERS: readonly string[] = ['all', 'company', 'archived']

export function filterTemplates(
  templates: readonly TemplateListItem[],
  filter: TemplateFilter,
  search: string,
): TemplateListItem[] {
  const term = search.trim().toLowerCase()
  return templates.filter((template) => {
    // Archived templates show only under their own pill, and only there.
    if (template.archived !== (filter === 'archived')) return false
    if (filter === 'company' && template.source !== 'company') return false
    if (!OWN_FILTERS.includes(filter) && !template.categories.includes(filter)) {
      return false
    }
    if (term === '') return true
    return [template.title, template.description, template.when_to_use].some((text) =>
      text.toLowerCase().includes(term),
    )
  })
}

/**
 * The "All" view's sections, one per group in the order given, each holding the
 * templates its pill would show. A template filed under several categories appears
 * in each; empty groups are dropped.
 */
export function groupTemplates<G extends { id: TemplateFilter }>(
  templates: readonly TemplateListItem[],
  groups: readonly G[],
): (G & { templates: TemplateListItem[] })[] {
  return groups
    .map((group) => ({ ...group, templates: filterTemplates(templates, group.id, '') }))
    .filter((group) => group.templates.length > 0)
}

/** The categories at least one unarchived template is filed under, in vocabulary order. */
export function usedCategories(
  templates: readonly TemplateListItem[],
  categories: readonly TemplateCategory[],
): TemplateCategory[] {
  const used = new Set(
    templates
      .filter((template) => !template.archived)
      .flatMap((template) => template.categories),
  )
  return categories.filter((category) => used.has(category.id))
}

export type InstalledTarget =
  | { to: '/dashboard/$dashboardId'; params: { dashboardId: number } }
  | {
      to: '/database/$databaseId/table/$tableId'
      params: { databaseId: number; tableId: number }
    }
  | { to: '/dashboard' }

/**
 * Where to go once a template is installed: its dashboard when it brought one,
 * since that is the part that shows the whole template working, and otherwise
 * the first table of its first database.
 */
export function installedTarget(applications: readonly Application[]): InstalledTarget {
  const dashboard = applications.find((application) => application.type === 'dashboard')
  if (dashboard !== undefined) {
    return { to: '/dashboard/$dashboardId', params: { dashboardId: dashboard.id } }
  }
  for (const application of applications) {
    if (application.type !== 'database') continue
    const table = byOrder(application.tables)[0]
    if (table !== undefined) {
      return {
        to: '/database/$databaseId/table/$tableId',
        params: { databaseId: application.id, tableId: table.id },
      }
    }
  }
  return { to: '/dashboard' }
}
